import type { ComponentType } from 'react';
import type { LinkMetadata } from '@/lib/fetch-metadata';
import { type ParseableTweet, parseTweetText } from '@/lib/tweet-parser';
import { cn } from '@/lib/utils';
import { normalizeTweetMediaItems } from '@/lib/validation/transformers/media-ui';
import type { Tweet } from '@/types/tweet';
import { buildMentionProfileLookup } from './mention-profile';
import { TweetAvatar } from './TweetAvatar';
import { TweetBody } from './TweetBody';
import { TweetHeader } from './TweetHeader';

interface QuoteTweetProperties {
  className?: string;
  inlineTweetComponent?: ComponentType<{ tweetId: string; fallbackUrl?: string }>;
  tweet: Tweet & { metadata?: Record<string, LinkMetadata> };
}

export function QuoteTweet({ tweet, className = '', inlineTweetComponent }: QuoteTweetProperties) {
  if (!tweet.author) {
    return (
      <div className="mt-2 border border-border/60/70 dark:border-white/10 rounded-xl p-3 text-muted-foreground text-sm">
        Unable to display quoted tweet
      </div>
    );
  }

  const parseableTweet: ParseableTweet = {
    text: tweet.text,
  };
  if (tweet.metadata) {
    parseableTweet.metadata = tweet.metadata;
  }
  const normalizedMediaItems = normalizeTweetMediaItems(tweet.mediaItems);
  if (normalizedMediaItems) {
    parseableTweet.mediaItems = normalizedMediaItems;
  }
  const { html: parsedHtml, cardUrls } = parseTweetText(parseableTweet);
  const mentionProfileLookup = buildMentionProfileLookup(tweet);

  return (
    <div
      className={cn(
        'mt-2 border border-border/60/70 dark:border-white/10 rounded-xl p-3 hover:bg-muted/30 transition-colors',
        className
      )}
    >
      <div className="flex gap-2">
        <div className="flex-shrink-0">
          <TweetAvatar author={tweet.author} size="xs" />
        </div>

        <div className="flex-1 min-w-0">
          <TweetHeader author={tweet.author} createdAt={tweet.createdAt} tweetId={tweet.id} compact={true} />

          <TweetBody
            parsedHtml={parsedHtml}
            {...(tweet.mediaItems ? { mediaItems: tweet.mediaItems } : {})}
            cardUrls={cardUrls}
            {...(tweet.metadata ? { metadata: tweet.metadata } : {})}
            compact={true}
            {...(inlineTweetComponent ? { inlineTweetComponent } : {})}
            {...(mentionProfileLookup ? { mentionProfiles: mentionProfileLookup } : {})}
          />
        </div>
      </div>
    </div>
  );
}
